import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, Search, FileText, Tag, Filter, Loader2, Database, Folder } from 'lucide-react';
import { KnowledgeFile, SearchType, Tag as TagType } from '../../types';
import { useFileStore } from '../../stores/file-store';
import { useCategoryStore } from '../../stores/category-store';
import { useTagStore } from '../../stores/tag-store';
import FileSearchResult from './FileSearchResult';
import { useI18n } from '../../i18n';
import { getFileTypeFromExtension } from '../../utils/file-types';

interface SearchModalProps {
  onClose: () => void;
  /** 可选：指定在某个分类下搜索（包含子分类） */
  categoryId?: string;
}

const SearchModal: React.FC<SearchModalProps> = ({ onClose, categoryId: propCategoryId }) => {
  const { 
    searchQuery, setSearchQuery, 
    searchType, setSearchType,
    search, filteredFiles, files, resetSearch,
    selectedTags, toggleTagSelection,
    searchInCategory
  } = useFileStore();
  const { activeCategory } = useCategoryStore();
  const { tags } = useTagStore();
  const { t } = useI18n();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // 服务端搜索结果
  const [serverResults, setServerResults] = useState<KnowledgeFile[]>([]);
  const [serverResultsCount, setServerResultsCount] = useState(0);
  const [useServerSearch, setUseServerSearch] = useState(false);
  
  // 如果有 propCategoryId 或 activeCategory，自动启用服务端搜索
  const targetCategoryId = propCategoryId || activeCategory?.id;
  const targetCategoryName = activeCategory?.name;

  // Get unique file types from actual files
  const availableFileTypes = React.useMemo(() => {
    const types = new Set(files.map(file => file.type));
    return Array.from(types).sort();
  }, [files]);
  
  // Focus the input on initial render
  useEffect(() => {
    inputRef.current?.focus();
    
    // Close modal on Escape key
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  
  // 服务端搜索
  const performServerSearch = useCallback(async (query: string) => {
    if (!query || query.length < 1) {
      setServerResults([]);
      setServerResultsCount(0);
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await searchInCategory(query, {
        categoryId: targetCategoryId,
        limit: 50
      });
      setServerResults(result.files);
      setServerResultsCount(result.totalCount);
    } catch (error) {
      console.error('服务端搜索失败:', error);
      setServerResults([]);
      setServerResultsCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchInCategory, targetCategoryId]);
  
  // Perform search when query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length > 0) {
        if (useServerSearch || targetCategoryId) {
          // 使用服务端搜索
          performServerSearch(searchQuery);
        } else {
          // 使用本地搜索
          setIsLoading(true);
          search();
          setIsLoading(false);
        }
      } else {
        setServerResults([]);
        setServerResultsCount(0);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, searchType, selectedTags, search, useServerSearch, targetCategoryId, performServerSearch]);
  
  const handleSearchTypeChange = (type: SearchType) => {
    setSearchType(type);
  };
  
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 决定显示哪些结果
  const displayResults = (useServerSearch || targetCategoryId) ? serverResults : filteredFiles;
  const displayCount = (useServerSearch || targetCategoryId) ? serverResultsCount : filteredFiles.length;
  const isServerMode = useServerSearch || !!targetCategoryId;
  
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[10vh]"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="relative">
          {/* 搜索头部 */}
          <div className="flex items-center px-4 border-b">
            <Search size={20} className="text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              className="w-full px-3 py-4 text-base text-gray-800 bg-transparent border-none focus:outline-none"
              placeholder={targetCategoryId 
                ? `在「${targetCategoryName || '当前文件夹'}」中搜索文件...` 
                : t('searchModalPlaceholder')
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {/* 服务端搜索指示器 */}
            {isServerMode && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-sky-50 text-sky-600 rounded-full text-xs font-medium mr-2">
                <Database size={12} />
                <span>全量搜索</span>
              </div>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <Filter size={18} className={showFilters ? 'text-teal-600' : ''} />
            </button>
            <button
              onClick={onClose}
              className="p-2 ml-1 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <X size={18} />
            </button>
          </div>
          
          {/* 搜索模式提示 */}
          {targetCategoryId && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-sm text-amber-700 flex items-center gap-2">
              <Folder size={16} />
              <span>正在搜索「{targetCategoryName || '当前文件夹'}」及其子文件夹中的所有文件</span>
            </div>
          )}
          
          {/* 搜索模式切换 */}
          {!targetCategoryId && (
            <div className="px-4 py-2 bg-slate-50 border-b flex items-center gap-3 text-sm">
              <span className="text-slate-500">搜索范围:</span>
              <button
                onClick={() => setUseServerSearch(false)}
                className={`px-3 py-1 rounded-full transition-colors ${
                  !useServerSearch
                    ? 'bg-sky-100 text-sky-700 font-medium'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                已加载文件
              </button>
              <button
                onClick={() => setUseServerSearch(true)}
                className={`px-3 py-1 rounded-full transition-colors flex items-center gap-1.5 ${
                  useServerSearch
                    ? 'bg-sky-100 text-sky-700 font-medium'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <Database size={12} />
                所有文件（服务端）
              </button>
            </div>
          )}
          
          {showFilters && !isServerMode && (
            <div className="p-3 border-b">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-sm font-medium text-gray-700">{t('searchBy')}</span>
                <button
                  onClick={() => handleSearchTypeChange('basic')}
                  className={`px-3 py-1 text-sm rounded-full ${
                    searchType === 'basic'
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('tabAll')}
                </button>
                <button
                  onClick={() => handleSearchTypeChange('tag')}
                  className={`px-3 py-1 text-sm rounded-full ${
                    searchType === 'tag'
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('tabTags')}
                </button>
                <button
                  onClick={() => handleSearchTypeChange('type')}
                  className={`px-3 py-1 text-sm rounded-full ${
                    searchType === 'type'
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('tabType')}
                </button>
                <button
                  onClick={() => handleSearchTypeChange('combined')}
                  className={`px-3 py-1 text-sm rounded-full ${
                    searchType === 'combined'
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('tabCombined')}
                </button>
              </div>
              
              {searchType === 'tag' && (
                <div>
                  <div className="mb-2 text-sm font-medium text-gray-700">{t('filterByTags')}</div>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => {
                          toggleTagSelection(tag);
                          if (selectedTags.some(t => t.id === tag.id)) {
                            // If tag was selected, now it will be deselected
                            if (selectedTags.length === 1) {
                              // If this was the last selected tag, reset search
                              resetSearch();
                            }
                          }
                          search();
                        }}
                        className={`flex items-center px-3 py-1 text-sm rounded-full transition-colors ${
                          selectedTags.some(t => t.id === tag.id)
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span
                          className="w-2 h-2 mr-1.5 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        ></span>
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            
              {searchType === 'type' && (
                <div>
                  <div className="mb-2 text-sm font-medium text-gray-700">{t('filterByType')}</div>
                  <div className="flex flex-wrap gap-2">
                    {availableFileTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => {
                          // Toggle selection
                          setSearchQuery(searchQuery === type ? '' : type);
                          search();
                        }}
                        className={`flex items-center px-3 py-1 text-sm rounded-full transition-colors ${
                          searchQuery === type
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span className="capitalize">
                          {type}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* 搜索结果 */}
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-8">
              <Loader2 size={32} className="text-sky-500 animate-spin mb-3" />
              <p className="text-sm text-gray-500">
                {isServerMode ? '正在搜索数据库...' : '搜索中...'}
              </p>
            </div>
          ) : searchQuery === '' && selectedTags.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <FileText size={48} className="text-gray-300 mb-4" />
              <p className="text-gray-500">{t('searchHelpStart')}</p>
              <p className="mt-2 text-sm text-gray-400">{t('searchHelpHint')}</p>
              {isServerMode && (
                <p className="mt-3 text-xs text-sky-500 bg-sky-50 px-3 py-1.5 rounded-full">
                  💡 服务端搜索支持搜索所有文件，无需先加载
                </p>
              )}
            </div>
          ) : displayResults.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <Search size={48} className="text-gray-300 mb-4" />
              <p className="text-gray-500">{t('searchNoResultsFor', { query: searchQuery } as any)}</p>
              <p className="mt-2 text-sm text-gray-400">{t('searchAdjust')}</p>
              {isServerMode && targetCategoryId && (
                <button
                  onClick={() => {
                    // 切换到全局搜索
                    setUseServerSearch(true);
                    performServerSearch(searchQuery);
                  }}
                  className="mt-4 text-sm text-sky-600 hover:text-sky-700 underline"
                >
                  尝试在所有文件中搜索
                </button>
              )}
            </div>
          ) : (
            <div className="p-2">
              <div className="pb-2 pl-2 text-sm font-medium text-gray-500 flex items-center justify-between">
                <span>
                  {t('viewAllResults', { count: displayResults.length } as any)}
                  {displayCount > displayResults.length && (
                    <span className="text-gray-400 ml-1">
                      （共 {displayCount} 个匹配）
                    </span>
                  )}
                </span>
                {isServerMode && (
                  <span className="text-xs text-sky-500 bg-sky-50 px-2 py-0.5 rounded">
                    来自数据库
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {displayResults.slice(0, 20).map((file) => (
                  <FileSearchResult key={file.id} file={file} onClose={onClose} />
                ))}
              </div>
              {displayResults.length > 20 && (
                <div className="pt-2 pb-1 text-center">
                  <span className="text-sm text-gray-500">
                    显示前 20 条结果，共 {displayResults.length} 条
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between p-3 text-xs text-gray-500 border-t bg-slate-50">
          <div className="flex items-center gap-4">
            <span>{t('escToClose')}</span>
            <span>{t('arrowsToNavigate')}</span>
          </div>
          {isServerMode && (
            <div className="flex items-center gap-1 text-sky-600">
              <Database size={12} />
              <span>服务端搜索已启用</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;